import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { fmt, fmtDate } from '../../lib/constants';
import Modal from '../../components/ui/Modal';
import StatCard from '../../components/ui/StatCard';
import toast from 'react-hot-toast';

// ── Sub-page keys ─────────────────────────────────────────────
const PAGES = [
  { key:'overview',      label:'📊 Overview',             group:'DASHBOARD' },
  { key:'contributions', label:'💰 Contributions',        group:'INCOME' },
  { key:'income',        label:'📥 Other Income',         group:'INCOME' },
  { key:'expenses',      label:'📤 Expenses',             group:'EXPENSES' },
  { key:'petty_cash',    label:'💵 Petty Cash',           group:'EXPENSES' },
  { key:'budgets',       label:'📋 Budgets',              group:'PLANNING' },
  { key:'accounts',      label:'📑 Chart of Accounts',    group:'PLANNING' },
  { key:'reports',       label:'📄 Financial Reports',    group:'REPORTS' },
  { key:'audit',         label:'🔍 Audit Log',            group:'REPORTS' },
];

const PAYMENT_METHODS = ['cash','mpesa','bank_transfer','cheque','card'];
const PM_LABELS = { cash:'💵 Cash', mpesa:'📱 M-Pesa', bank_transfer:'🏦 Bank Transfer', cheque:'📃 Cheque', card:'💳 Card' };

const CONTRIBUTION_TYPES = ['Tithe','Offering','Thanksgiving','Harvest','Building Fund','Special'];
const INCOME_CATEGORIES   = ['rent','donation','grant','fundraiser','interest','harvest_sale','other'];

const JOURNALS = ['general','groups','office','admin','hospitality','grounds','missions','retreat','petty_cash','opex','capex','other'];

export default function FinancialModulePage() {
  const { profile } = useAuth();
  const [page, setPage] = useState('overview');

  return (
    <div style={{ display:'flex', gap:0 }}>
      {/* Left sub-nav */}
      <div style={{ width:200, flexShrink:0, marginRight:20 }}>
        <div className="card" style={{ overflow:'hidden', position:'sticky', top:80 }}>
          {['DASHBOARD','INCOME','EXPENSES','PLANNING','REPORTS'].map(grp => {
            const items = PAGES.filter(p => p.group === grp);
            return (
              <div key={grp}>
                <div style={{ padding:'9px 12px 4px', fontSize:10, fontWeight:700, letterSpacing:.8, textTransform:'uppercase', color:'var(--text3)', background:'var(--bg)' }}>{grp}</div>
                {items.map(p => (
                  <button key={p.key} onClick={() => setPage(p.key)}
                    style={{ display:'flex', alignItems:'center', width:'100%', padding:'9px 14px', background: page===p.key ? 'var(--blue-light)' : 'white', border:'none', borderLeft:`3px solid ${page===p.key?'var(--blue)':'transparent'}`, fontSize:12, fontWeight: page===p.key ? 700 : 400, color: page===p.key ? 'var(--blue-dark)' : 'var(--text)', cursor:'pointer', textAlign:'left', borderBottom:'1px solid var(--border)', transition:'all .12s' }}>
                    {p.label}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex:1, minWidth:0 }}>
        {page === 'overview'      && <OverviewPanel />}
        {page === 'contributions' && <ContributionsPanel />}
        {page === 'income'        && <OtherIncomePanel />}
        {page === 'expenses'      && <ExpensesPanel />}
        {page === 'petty_cash'    && <PettyCashPanel />}
        {page === 'budgets'       && <BudgetsPanel />}
        {page === 'accounts'      && <AccountsPanel />}
        {page === 'reports'       && <FinancialReportsPanel />}
        {page === 'audit'         && <AuditPanel />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// OVERVIEW
// ════════════════════════════════════════════════════════════
function OverviewPanel() {
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [year, month]);

  async function load() {
    setLoading(true);
    const mStr = `${year}-${String(month).padStart(2,'0')}`;
    const [
      { data: contribsMonth },
      { data: contribsYear  },
      { data: expensesMonth },
      { data: expensesYear  },
      { data: incomeMonth   },
      { data: distContribs  },
    ] = await Promise.all([
      supabase.from('contributions').select('type,amount').gte('date',`${mStr}-01`).lte('date',`${mStr}-31`),
      supabase.from('contributions').select('type,amount').gte('date',`${year}-01-01`).lte('date',`${year}-12-31`),
      supabase.from('expenses').select('amount,journal').gte('date',`${mStr}-01`).lte('date',`${mStr}-31`),
      supabase.from('expenses').select('amount,journal').gte('date',`${year}-01-01`).lte('date',`${year}-12-31`),
      supabase.from('income_entries').select('amount').gte('date',`${mStr}-01`).lte('date',`${mStr}-31`),
      supabase.from('contributions').select('amount,type,members(districts(name))').gte('date',`${mStr}-01`).lte('date',`${mStr}-31`),
    ]);

    const sumAmt = (arr) => (arr||[]).reduce((s,r) => s+Number(r.amount),0);
    const sumType = (arr,type) => (arr||[]).filter(r=>r.type===type).reduce((s,r)=>s+Number(r.amount),0);

    // District breakdown
    const byDistrict = {};
    (distContribs||[]).forEach(c => {
      const d = c.members?.districts?.name || 'Unknown';
      if (!byDistrict[d]) byDistrict[d] = { tithe:0, offering:0, total:0 };
      byDistrict[d][c.type==='Tithe'?'tithe':'offering'] += Number(c.amount);
      byDistrict[d].total += Number(c.amount);
    });

    const monthIncome  = sumAmt(contribsMonth) + sumAmt(incomeMonth);
    const monthExpense = sumAmt(expensesMonth);
    const yearIncome   = sumAmt(contribsYear);
    const yearExpense  = sumAmt(expensesYear);

    // Monthly trend (12 months)
    const trend = await Promise.all(Array.from({length:12},(_,i)=>{
      const m = String(i+1).padStart(2,'0');
      return Promise.all([
        supabase.from('contributions').select('amount').gte('date',`${year}-${m}-01`).lte('date',`${year}-${m}-31`),
        supabase.from('expenses').select('amount').gte('date',`${year}-${m}-01`).lte('date',`${year}-${m}-31`),
      ]).then(([c,e])=>({
        month: new Date(year,i,1).toLocaleString('default',{month:'short'}),
        income:  (c.data||[]).reduce((s,r)=>s+Number(r.amount),0),
        expense: (e.data||[]).reduce((s,r)=>s+Number(r.amount),0),
      }));
    }));

    setData({
      monthIncome, monthExpense, monthSurplus: monthIncome-monthExpense,
      yearIncome, yearExpense, yearSurplus: yearIncome-yearExpense,
      monthTithe: sumType(contribsMonth,'Tithe'), monthOffering: sumType(contribsMonth,'Offering'),
      yearTithe:  sumType(contribsYear, 'Tithe'), yearOffering:  sumType(contribsYear, 'Offering'),
      byDistrict, trend,
    });
    setLoading(false);
  }

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (loading||!data) return <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading…</div>;
  const maxTrend = Math.max(...data.trend.map(t=>Math.max(t.income,t.expense)),1);

  return (
    <div>
      <div className="flex-between mb-4">
        <h1 style={{fontSize:20,fontWeight:800}}>📊 Financial Overview</h1>
        <div className="flex gap-2">
          <select className="form-select" style={{width:'auto'}} value={month} onChange={e=>setMonth(Number(e.target.value))}>
            {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
          </select>
          <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>
            {[2026,2025,2024,2023].map(y=><option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Month stats */}
      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>
        {MONTHS[month-1]} {year} — Monthly Summary
      </div>
      <div className="grid-4 mb-4">
        <StatCard icon="📥" label="Month Income"  value={fmt(data.monthIncome)}   color="green" />
        <StatCard icon="📤" label="Month Expenses" value={fmt(data.monthExpense)}  color="red" />
        <StatCard icon="🙏" label="Month Tithes"  value={fmt(data.monthTithe)}    color="blue" />
        <StatCard icon="🎁" label="Month Offering" value={fmt(data.monthOffering)} color="amber" />
      </div>

      {/* Year stats */}
      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5,marginBottom:10}}>
        {year} — Annual Summary
      </div>
      <div className="grid-4 mb-4">
        <StatCard icon="💰" label="Year Income"   value={fmt(data.yearIncome)}   color="green" />
        <StatCard icon="💸" label="Year Expenses" value={fmt(data.yearExpense)}  color="red" />
        <StatCard icon="🏦" label="Year Surplus"  value={fmt(data.yearSurplus)}  color={data.yearSurplus>=0?'blue':'red'} />
        <StatCard icon="📈" label="Year Tithes"   value={fmt(data.yearTithe)}    color="blue" />
      </div>

      <div className="grid-2 mb-4">
        {/* Trend chart */}
        <div className="card">
          <div className="card-header"><h3>Monthly Income vs Expenses — {year}</h3></div>
          <div className="card-pad">
            <div style={{display:'flex',alignItems:'flex-end',gap:4,height:140,marginBottom:8}}>
              {data.trend.map((t,i)=>(
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                  <div style={{width:'100%',display:'flex',flexDirection:'column',alignItems:'center',gap:2,height:120,justifyContent:'flex-end'}}>
                    <div style={{width:'45%',height:`${Math.round((t.income/maxTrend)*100)}%`,background:'var(--blue)',borderRadius:'3px 3px 0 0',minHeight:2,transition:'height .3s'}} title={`Income: ${fmt(t.income)}`} />
                    <div style={{width:'45%',height:`${Math.round((t.expense/maxTrend)*100)}%`,background:'var(--red)',borderRadius:'3px 3px 0 0',minHeight:2,transition:'height .3s'}} title={`Expense: ${fmt(t.expense)}`} />
                  </div>
                  <div style={{fontSize:9,color:'var(--text3)',fontWeight:600}}>{t.month}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-3" style={{justifyContent:'center'}}>
              {[['var(--blue)','Income'],['var(--red)','Expenses']].map(([c,l])=>(
                <div key={l} className="flex-center gap-1" style={{fontSize:11}}><div style={{width:10,height:10,borderRadius:2,background:c}}/> {l}</div>
              ))}
            </div>
          </div>
        </div>

        {/* District breakdown */}
        <div className="card">
          <div className="card-header"><h3>Contributions by District — {MONTHS[month-1]}</h3></div>
          <div className="card-pad">
            {Object.keys(data.byDistrict).length === 0
              ? <div style={{color:'var(--text3)',fontSize:13}}>No contributions this month.</div>
              : Object.entries(data.byDistrict).sort((a,b)=>b[1].total-a[1].total).map(([d,v])=>(
                <div key={d} style={{marginBottom:12}}>
                  <div className="flex-between mb-1">
                    <span style={{fontSize:13,fontWeight:600}}>{d}</span>
                    <span style={{fontSize:13,fontWeight:700,color:'var(--blue-dark)'}}>{fmt(v.total)}</span>
                  </div>
                  <div style={{display:'flex',gap:4,fontSize:11,color:'var(--text3)'}}>
                    <span>Tithe: {fmt(v.tithe)}</span>
                    <span>·</span>
                    <span>Offering: {fmt(v.offering)}</span>
                  </div>
                  <div style={{height:6,background:'var(--bg)',borderRadius:3,marginTop:4,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${Math.round((v.total/Math.max(...Object.values(data.byDistrict).map(x=>x.total),1))*100)}%`,background:'linear-gradient(to right,var(--blue),var(--red))',borderRadius:3}} />
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CONTRIBUTIONS PANEL
// Paste this inside FinancialModulePage.jsx replacing the
// existing function ContributionsPanel() { ... }
// ════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════
// CONTRIBUTIONS PANEL
// FILE: src/pages/admin/ContributionsPanel.jsx
// Paste this inside FinancialModulePage.jsx replacing the
// existing function ContributionsPanel() { ... }
//
// UPDATED: Added income types from Sunday Received Income
// Analysis Template — Effort Day, Foundation Day, Redemption,
// Church School, Gifts, Others
// ════════════════════════════════════════════════════════════

function ContributionsPanel() {
  const { profile } = useAuth();

  // ── UPDATED: matches Sunday Received Income Analysis Template ─
  // Template columns: Offerings, Tithes, Thanks Giving,
  // Effort Day's, Foundation Day, Redem, Church School, Gifts, Others
  const CONTRIBUTION_TYPES = [
    'Tithe',
    'Offering',
    'Thanksgiving',
    'Effort Day',       // ← template: "Effort Day's"
    'Foundation Day',   // ← template: "Foundation Day"
    'Redemption',       // ← template: "Redem"
    'Church School',    // ← template: "Church School"
    'Gifts',            // ← template: "Gifts"
    'Harvest',
    'Building Fund',
    'Special',
    'Others',
  ];

  const PAYMENT_METHODS = ['cash', 'mpesa', 'bank_transfer', 'cheque', 'card'];
  const PM_LABELS = {
    cash: '💵 Cash', mpesa: '📱 M-Pesa', bank_transfer: '🏦 Bank',
    cheque: '📃 Cheque', card: '💳 Card',
  };
  const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // ── View / filter state ───────────────────────────────────────
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [month,     setMonth]     = useState('');
  const [entryType, setEntryType] = useState('');
  const [districtF, setDistrictF] = useState('');
  const [typeF,     setTypeF]     = useState('');
  const [records,   setRecords]   = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [summary,   setSummary]   = useState({});

  // ── Record mode ───────────────────────────────────────────────
  const [showRecord, setShowRecord] = useState(false);
  const [recordMode, setRecordMode] = useState('member');
  const [saving,     setSaving]     = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const blankShared = {
    date: today, type: 'Offering', amount: '',
    payment_method: 'cash', reference_no: '', notes: '',
  };

  const [memberForm,    setMemberForm]    = useState({ ...blankShared });
  const [memberSearch,  setMemberSearch]  = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selMember,     setSelMember]     = useState(null);
  const [distForm,      setDistForm]      = useState({ ...blankShared, district_id: '' });
  const [sundayForm,    setSundayForm]    = useState({ ...blankShared });

  useEffect(() => {
    supabase.from('districts').select('*').order('name').then(({ data }) => setDistricts(data || []));
    loadRecords();
  }, [year, month, entryType, districtF, typeF]);

  async function loadRecords() {
    setLoading(true);
    const mStr   = month ? `${year}-${String(month).padStart(2, '0')}` : '';
    const dStart = month ? `${mStr}-01` : `${year}-01-01`;
    const dEnd   = month ? `${mStr}-31` : `${year}-12-31`;

    let q = supabase.from('contributions')
      .select(`*, members(first_name,last_name,member_number), districts:district_id(name)`)
      .gte('date', dStart).lte('date', dEnd)
      .order('date', { ascending: false });

    if (entryType) q = q.eq('entry_type', entryType);
    if (typeF)     q = q.eq('type', typeF);
    if (districtF) q = q.eq('district_id', districtF);

    const { data } = await q;
    const rows = data || [];
    setRecords(rows);

    const sumAmt = (arr) => arr.reduce((s, r) => s + Number(r.amount), 0);
    const total  = sumAmt(rows);
    const byType = {};
    rows.forEach(r => { byType[r.entry_type] = (byType[r.entry_type] || 0) + Number(r.amount); });

    // Per contribution type totals for summary
    const byContribType = {};
    CONTRIBUTION_TYPES.forEach(t => {
      byContribType[t] = rows.filter(r => r.type === t).reduce((s, r) => s + Number(r.amount), 0);
    });

    setSummary({
      total,
      tithes:   rows.filter(r => r.type === 'Tithe').reduce((s, r) => s + Number(r.amount), 0),
      offer:    rows.filter(r => r.type === 'Offering').reduce((s, r) => s + Number(r.amount), 0),
      byType,
      byContribType,
    });
    setLoading(false);
  }

  async function searchMembers(val) {
    setMemberSearch(val);
    if (val.length < 2) { setMemberResults([]); return; }
    const { data } = await supabase.from('members')
      .select('id, first_name, last_name, member_number, districts:district_id(name)')
      .or(`first_name.ilike.%${val}%,last_name.ilike.%${val}%,member_number.ilike.%${val}%`)
      .eq('status', 'active').limit(6);
    setMemberResults(data || []);
  }

  function resetForms() {
    setSelMember(null); setMemberSearch(''); setMemberResults([]);
    setMemberForm({ ...blankShared });
    setDistForm({ ...blankShared, district_id: '' });
    setSundayForm({ ...blankShared });
  }

  async function saveMember(e) {
    e.preventDefault();
    if (!selMember) { toast.error('Select a member first'); return; }
    setSaving(true);
    const { error } = await supabase.from('contributions').insert({
      entry_type: 'member', member_id: selMember.id,
      date: memberForm.date, type: memberForm.type,
      amount: Number(memberForm.amount),
      payment_method: memberForm.payment_method,
      reference_no: memberForm.reference_no || '',
      notes: memberForm.notes || '',
      recorded_by: profile?.id,
    });
    if (error) { toast.error(error.message); setSaving(false); return; }
    toast.success('✅ Saved');
    setSuccessMsg(`${selMember.first_name} ${selMember.last_name} — ${memberForm.type} — KES ${Number(memberForm.amount).toLocaleString()}`);
    resetForms(); loadRecords(); setSaving(false);
  }

  async function saveDistrict(e) {
    e.preventDefault();
    if (!distForm.district_id) { toast.error('Select a district'); return; }
    setSaving(true);
    const { error } = await supabase.from('contributions').insert({
      entry_type: 'district', member_id: null,
      district_id: distForm.district_id,
      date: distForm.date, type: distForm.type,
      amount: Number(distForm.amount),
      payment_method: distForm.payment_method,
      reference_no: distForm.reference_no || '',
      notes: distForm.notes || '',
      recorded_by: profile?.id,
    });
    if (error) { toast.error(error.message); setSaving(false); return; }
    const dname = districts.find(d => d.id === distForm.district_id)?.name || '';
    toast.success('✅ Saved');
    setSuccessMsg(`${dname} District — ${distForm.type} — KES ${Number(distForm.amount).toLocaleString()}`);
    resetForms(); loadRecords(); setSaving(false);
  }

  async function saveSunday(e) {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.from('contributions').insert({
      entry_type: 'sunday_total', member_id: null, district_id: null,
      service_date: sundayForm.date,
      date: sundayForm.date, type: sundayForm.type,
      amount: Number(sundayForm.amount),
      payment_method: sundayForm.payment_method,
      reference_no: sundayForm.reference_no || '',
      notes: sundayForm.notes || '',
      recorded_by: profile?.id,
    });
    if (error) { toast.error(error.message); setSaving(false); return; }
    toast.success('✅ Saved');
    setSuccessMsg(`Sunday ${sundayForm.date} — ${sundayForm.type} (Church Total) — KES ${Number(sundayForm.amount).toLocaleString()}`);
    resetForms(); loadRecords(); setSaving(false);
  }

  async function deleteContrib(id) {
    if (!window.confirm('Delete this entry?')) return;
    await supabase.from('contributions').delete().eq('id', id);
    toast.success('Deleted'); loadRecords();
  }

  const setM = k => e => setMemberForm(f => ({ ...f, [k]: e.target.value }));
  const setD = k => e => setDistForm(f => ({ ...f, [k]: e.target.value }));
  const setS = k => e => setSundayForm(f => ({ ...f, [k]: e.target.value }));

  // ── Type select with grouping ─────────────────────────────────
  const TypeSelect = ({ value, onChange }) => (
    <div className="form-group">
      <label className="form-label">Type *</label>
      <select className="form-select" value={value} onChange={onChange}>
        <optgroup label="── Regular ──────────────">
          <option>Tithe</option>
          <option>Offering</option>
          <option>Thanksgiving</option>
        </optgroup>
        <optgroup label="── Special Collections ──">
          <option>Effort Day</option>
          <option>Foundation Day</option>
          <option>Redemption</option>
          <option>Church School</option>
          <option>Gifts</option>
          <option>Harvest</option>
          <option>Building Fund</option>
          <option>Special</option>
        </optgroup>
        <optgroup label="── Other ────────────────">
          <option>Others</option>
        </optgroup>
      </select>
    </div>
  );

  const PayRow = ({ form, setter }) => (
    <div className="grid-2" style={{ marginTop: 8 }}>
      <div className="form-group">
        <label className="form-label">Payment Method</label>
        <select className="form-select" value={form.payment_method} onChange={setter('payment_method')}>
          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{PM_LABELS[m]}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Reference / Receipt</label>
        <input className="form-input" value={form.reference_no} onChange={setter('reference_no')} placeholder="M-Pesa ref, cheque no…"/>
      </div>
      <div className="form-group" style={{ gridColumn: '1/-1' }}>
        <label className="form-label">Notes</label>
        <input className="form-input" value={form.notes} onChange={setter('notes')} placeholder="Optional notes…"/>
      </div>
    </div>
  );

  const EntryBadge = ({ type }) => {
    const map = {
      member:       { label: '👤 Member',      bg: '#E3F2FD', color: '#003399' },
      district:     { label: '📍 District',     bg: '#E8F5E9', color: '#1B5E20' },
      sunday_total: { label: '⛪ Sunday Total', bg: '#FFF8E0', color: '#C47A00' },
    };
    const s = map[type] || { label: type, bg: '#F5F5F5', color: '#546E7A' };
    return (
      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 100, background: s.bg, color: s.color, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {s.label}
      </span>
    );
  };

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>💰 Contributions</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>Per member · Per district · Sunday totals</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowRecord(r => !r); setSuccessMsg(''); }}>
          {showRecord ? '✕ Close' : '+ Record Contribution'}
        </button>
      </div>

      {/* ── RECORD PANEL ── */}
      {showRecord && (
        <div className="card mb-4" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 18px', background: 'var(--blue-dark)', color: 'white', fontWeight: 700, fontSize: 13 }}>
            ✏️ Record New Contribution
          </div>

          {successMsg && (
            <div style={{ margin: '12px 18px', padding: '10px 14px', background: '#E8F5E9', borderRadius: 8, fontSize: 12, color: '#1B5E20', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              ✅ Recorded: {successMsg}
              <button onClick={() => setSuccessMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B5E20', fontSize: 16 }}>✕</button>
            </div>
          )}

          {/* Mode tabs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid var(--border)' }}>
            {[
              { key: 'member',       icon: '👤', label: 'Per Member' },
              { key: 'district',     icon: '📍', label: 'Per District' },
              { key: 'sunday_total', icon: '⛪', label: 'Sunday Total' },
            ].map(opt => (
              <button key={opt.key} onClick={() => setRecordMode(opt.key)}
                style={{
                  padding: '12px 10px', border: 'none',
                  borderBottom: `3px solid ${recordMode === opt.key ? 'var(--blue)' : 'transparent'}`,
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
                  fontWeight: recordMode === opt.key ? 700 : 400,
                  color: recordMode === opt.key ? 'var(--blue-dark)' : 'var(--text3)',
                  background: recordMode === opt.key ? 'var(--blue-light)' : 'white',
                  transition: 'all .15s',
                }}>
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>

          <div className="card-pad">
            {/* Per Member */}
            {recordMode === 'member' && (
              <form onSubmit={saveMember}>
                <div className="form-group">
                  <label className="form-label">Member *</label>
                  <input className="form-input" value={memberSearch}
                    onChange={e => searchMembers(e.target.value)}
                    placeholder="Search name, number or phone…"/>
                  {memberResults.length > 0 && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, overflow: 'hidden', background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,.1)' }}>
                      {memberResults.map(m => (
                        <div key={m.id}
                          onClick={() => { setSelMember(m); setMemberSearch(`${m.first_name} ${m.last_name}`); setMemberResults([]); }}
                          style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                          <span style={{ fontWeight: 700 }}>{m.first_name} {m.last_name}
                            <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 11, marginLeft: 6 }}>{m.member_number}</span>
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{m.districts?.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selMember && (
                    <div style={{ marginTop: 6, padding: '7px 12px', background: 'var(--blue-light)', borderRadius: 7, fontSize: 12, color: 'var(--blue-dark)', fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
                      <span>✓ {selMember.first_name} {selMember.last_name} — {selMember.member_number} — {selMember.districts?.name}</span>
                      <button type="button" onClick={() => { setSelMember(null); setMemberSearch(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 15 }}>✕</button>
                    </div>
                  )}
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input className="form-input" type="date" value={memberForm.date} onChange={setM('date')} required/>
                  </div>
                  <TypeSelect value={memberForm.type} onChange={setM('type')}/>
                  <div className="form-group">
                    <label className="form-label">Amount (KES) *</label>
                    <input className="form-input" type="number" min="1" value={memberForm.amount} onChange={setM('amount')} placeholder="0" required/>
                  </div>
                </div>
                <PayRow form={memberForm} setter={setM}/>
                <button type="submit" className="btn btn-primary btn-sm mt-2" disabled={saving}>{saving ? 'Saving…' : '💾 Save'}</button>
              </form>
            )}

            {/* Per District */}
            {recordMode === 'district' && (
              <form onSubmit={saveDistrict}>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input className="form-input" type="date" value={distForm.date} onChange={setD('date')} required/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">District *</label>
                    <select className="form-select" value={distForm.district_id} onChange={setD('district_id')} required>
                      <option value="">Select district…</option>
                      {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <TypeSelect value={distForm.type} onChange={setD('type')}/>
                  <div className="form-group">
                    <label className="form-label">Total Amount (KES) *</label>
                    <input className="form-input" type="number" min="1" value={distForm.amount} onChange={setD('amount')} placeholder="Total from this district" required/>
                  </div>
                </div>
                <PayRow form={distForm} setter={setD}/>
                <button type="submit" className="btn btn-sm mt-2" style={{ background: '#2E7D32', color: 'white', border: 'none' }} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save District Total'}
                </button>
              </form>
            )}

            {/* Sunday Total */}
            {recordMode === 'sunday_total' && (
              <form onSubmit={saveSunday}>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Sunday Date *</label>
                    <input className="form-input" type="date" value={sundayForm.date} onChange={setS('date')} required/>
                  </div>
                  <TypeSelect value={sundayForm.type} onChange={setS('type')}/>
                  <div className="form-group">
                    <label className="form-label">Church Total (KES) *</label>
                    <input className="form-input" type="number" min="1" value={sundayForm.amount} onChange={setS('amount')} placeholder="Total collected whole church" required/>
                  </div>
                </div>
                <PayRow form={sundayForm} setter={setS}/>
                <button type="submit" className="btn btn-sm mt-2" style={{ background: '#C47A00', color: 'white', border: 'none' }} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save Sunday Total'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── SUMMARY CARDS ── */}
      <div className="grid-4 mb-4">
        <StatCard icon="💰" label="Total" value={fmt(summary.total || 0)} color="blue"/>
        <StatCard icon="🙏" label="Tithes" value={fmt(summary.tithes || 0)} color="green"/>
        <StatCard icon="🎁" label="Offerings" value={fmt(summary.offer || 0)} color="amber"/>
        <div className="stat-card">
          <div className="stat-icon blue" style={{ fontSize: 12 }}>📊</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>
            {[['👤', summary.byType?.member || 0], ['📍', summary.byType?.district || 0], ['⛪', summary.byType?.sunday_total || 0]].map(([ic, v]) => (
              <div key={ic} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                <span style={{ color: 'var(--text3)' }}>{ic}</span>
                <strong>{fmt(v)}</strong>
              </div>
            ))}
          </div>
          <div className="stat-label" style={{ marginTop: 4 }}>By Entry Type</div>
        </div>
      </div>

      {/* ── FILTERS ── */}
      <div className="card mb-4">
        <div className="card-pad" style={{ paddingTop: 10, paddingBottom: 10 }}>
          <div className="flex gap-2 flex-wrap">
            <select className="form-select" style={{ width: 'auto' }} value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2026, 2025, 2024, 2023].map(y => <option key={y}>{y}</option>)}
            </select>
            <select className="form-select" style={{ width: 'auto' }} value={month} onChange={e => setMonth(e.target.value)}>
              <option value="">All Months</option>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select className="form-select" style={{ width: 'auto' }} value={entryType} onChange={e => setEntryType(e.target.value)}>
              <option value="">All Entry Types</option>
              <option value="member"> Per Member</option>
              <option value="district"> Per District</option>
              <option value="sunday_total"> Sunday Total</option>
            </select>
            <select className="form-select" style={{ width: 'auto' }} value={districtF} onChange={e => setDistrictF(e.target.value)}>
              <option value="">All Districts</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select className="form-select" style={{ width: 'auto' }} value={typeF} onChange={e => setTypeF(e.target.value)}>
              <option value="">All Types</option>
              <optgroup label="Regular">
                {['Tithe', 'Offering', 'Thanksgiving'].map(t => <option key={t}>{t}</option>)}
              </optgroup>
              <optgroup label="Special">
                {['Effort Day', 'Foundation Day', 'Redemption', 'Church School', 'Gifts', 'Harvest', 'Building Fund', 'Special', 'Others'].map(t => <option key={t}>{t}</option>)}
              </optgroup>
            </select>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
            {records.length} records · Total: <strong>{fmt(summary.total || 0)}</strong>
          </div>
        </div>
      </div>

      {/* ── TABLE ── */}
      {loading
        ? <div style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>Loading…</div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Entry Type</th>
                  <th>Member / District</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Ref / Notes</th>
                  <th>Del</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>No contributions found.</td></tr>
                )}
                {records.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                    <td><EntryBadge type={r.entry_type}/></td>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>
                      {r.entry_type === 'member'
                        ? r.members ? `${r.members.first_name} ${r.members.last_name}` : '—'
                        : r.entry_type === 'district'
                          ? r.districts ? `📍 ${r.districts.name}` : '—'
                          : '⛪ Whole Church'}
                      {r.entry_type === 'member' && r.members && (
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{r.members.member_number}</div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${['Tithe', 'Offering', 'Thanksgiving'].includes(r.type) ? 'blue' : 'gray'}`}
                        style={{ fontSize: 10 }}>
                        {r.type}
                      </span>
                    </td>
                    <td style={{ fontWeight: 700, color: 'var(--blue-dark)' }}>{fmt(r.amount)}</td>
                    <td style={{ fontSize: 12 }}>{PM_LABELS[r.payment_method] || r.payment_method || 'Cash'}</td>
                    <td style={{ fontSize: 11, color: 'var(--text3)' }}>{r.reference_no || r.notes || '—'}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteContrib(r.id)}>×</button>
                    </td>
                  </tr>
                ))}
                {records.length > 0 && (
                  <tr style={{ background: 'var(--blue-light)', fontWeight: 700 }}>
                    <td colSpan={4}>TOTAL</td>
                    <td>{fmt(summary.total || 0)}</td>
                    <td colSpan={3}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// OTHER INCOME
// ════════════════════════════════════════════════════════════
function OtherIncomePanel() {
  const {profile}=useAuth();
  const [records,setRecords]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showAdd,setShowAdd]=useState(false);
  const [saving,setSaving]=useState(false);
  const [year,setYear]=useState(new Date().getFullYear());
  const [form,setForm]=useState({date:new Date().toISOString().split('T')[0],description:'',amount:'',category:'other',payment_method:'cash',reference_no:'',notes:''});

  useEffect(()=>{load();},[year]);
  async function load(){
    setLoading(true);
    const {data}=await supabase.from('income_entries').select('*,districts(name)').gte('date',`${year}-01-01`).lte('date',`${year}-12-31`).order('date',{ascending:false});
    setRecords(data||[]);setLoading(false);
  }
  async function handleSave(e){
    e.preventDefault();setSaving(true);
    const {error}=await supabase.from('income_entries').insert({...form,amount:Number(form.amount),recorded_by:profile?.id});
    if(error)toast.error(error.message);
    else{toast.success('Income recorded ✅');setShowAdd(false);setForm({date:new Date().toISOString().split('T')[0],description:'',amount:'',category:'other',payment_method:'cash',reference_no:'',notes:''});load();}
    setSaving(false);
  }
  async function deleteRec(id){if(!window.confirm('Delete?'))return;await supabase.from('income_entries').delete().eq('id',id);toast.success('Deleted');load();}

  const total=records.reduce((s,r)=>s+Number(r.amount),0);

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1 style={{fontSize:20,fontWeight:800}}>📥 Other Income</h1><div style={{fontSize:13,color:'var(--text3)'}}>Rental income, donations, grants, fundraisers</div></div>
        <div className="flex gap-2">
          <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>{[2026,2025,2024,2023].map(y=><option key={y}>{y}</option>)}</select>
          <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ Add Income</button>
        </div>
      </div>
      <div className="grid-3 mb-4">
        <StatCard icon="📥" label="Total Other Income" value={fmt(total)} color="green"/>
        <StatCard icon="🏠" label="Rental" value={fmt(records.filter(r=>r.category==='rent').reduce((s,r)=>s+Number(r.amount),0))} color="blue"/>
        <StatCard icon="🎉" label="Fundraisers" value={fmt(records.filter(r=>r.category==='fundraiser').reduce((s,r)=>s+Number(r.amount),0))} color="amber"/>
      </div>
      {loading?<div style={{textAlign:'center',padding:28,color:'var(--text3)'}}>Loading…</div>:(
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Amount</th><th>Method</th><th>Reference</th><th>Del</th></tr></thead>
            <tbody>
              {records.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:28,color:'var(--text3)'}}>No income records.</td></tr>}
              {records.map(r=>(
                <tr key={r.id}>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(r.date)}</td>
                  <td style={{fontWeight:600}}>{r.description}</td>
                  <td><span className="badge badge-green" style={{textTransform:'capitalize'}}>{r.category}</span></td>
                  <td style={{fontWeight:700,color:'var(--blue-dark)'}}>{fmt(r.amount)}</td>
                  <td style={{fontSize:12}}>{PM_LABELS[r.payment_method]||r.payment_method}</td>
                  <td style={{fontSize:12,color:'var(--text3)'}}>{r.reference_no||'—'}</td>
                  <td><button className="btn btn-danger btn-sm" onClick={()=>deleteRec(r.id)}>×</button></td>
                </tr>
              ))}
              {records.length>0&&<tr style={{background:'var(--blue-light)',fontWeight:700}}><td colSpan={3}>TOTAL</td><td>{fmt(total)}</td><td colSpan={3}></td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {showAdd&&(
        <Modal title="Record Other Income" onClose={()=>setShowAdd(false)}
          footer={<><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="income-form" type="submit" disabled={saving}>{saving?'Saving…':'Save'}</button></>}>
          <form id="income-form" onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Category *</label><select className="form-select" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>{INCOME_CATEGORIES.map(c=><option key={c} value={c} style={{textTransform:'capitalize'}}>{c}</option>)}</select></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Description *</label><input className="form-input" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input className="form-input" type="number" min="1" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Payment Method</label><select className="form-select" value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))}>{PAYMENT_METHODS.map(m=><option key={m} value={m}>{PM_LABELS[m]}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Reference No</label><input className="form-input" value={form.reference_no} onChange={e=>setForm(f=>({...f,reference_no:e.target.value}))} placeholder="M-Pesa ref, cheque no…"/></div>
              <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// EXPENSES
// ════════════════════════════════════════════════════════════
function ExpensesPanel() {
  const {profile}=useAuth();
  const [records,setRecords]=useState([]);
  const [categories,setCategories]=useState([]);
  const [groups,setGroups]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showAdd,setShowAdd]=useState(false);
  const [saving,setSaving]=useState(false);
  const [year,setYear]=useState(new Date().getFullYear());
  const [month,setMonth]=useState('');
  const [journal,setJournal]=useState('');
  const [catF,setCatF]=useState('');
  const blank={date:new Date().toISOString().split('T')[0],description:'',amount:'',category_id:'',payment_method:'cash',reference_no:'',journal:'general',group_id:'',approved_by:'',receipt_no:'',notes:''};
  const [form,setForm]=useState({...blank});

  useEffect(()=>{
    supabase.from('expense_categories').select('*').order('name').then(({data})=>setCategories(data||[]));
    supabase.from('groups').select('id,name').order('name').then(({data})=>setGroups(data||[]));
    load();
  },[year,month,journal,catF]);

  async function load(){
    setLoading(true);
    let q=supabase.from('expenses').select('*,expense_categories(name,type),groups(name)').gte('date',`${year}-01-01`).lte('date',`${year}-12-31`).order('date',{ascending:false});
    if(month) q=q.gte('date',`${year}-${String(month).padStart(2,'0')}-01`).lte('date',`${year}-${String(month).padStart(2,'0')}-31`);
    if(journal) q=q.eq('journal',journal);
    if(catF) q=q.eq('category_id',catF);
    const {data}=await q; setRecords(data||[]); setLoading(false);
  }

  async function handleSave(e){
    e.preventDefault();setSaving(true);
    const payload={...form,amount:Number(form.amount),category_id:form.category_id||null,group_id:form.group_id||null,recorded_by:profile?.id};
    const {error}=await supabase.from('expenses').insert(payload);
    if(error)toast.error(error.message);
    else{toast.success('Expense recorded ✅');setShowAdd(false);setForm({...blank});load();}
    setSaving(false);
  }
  async function deleteExp(id){if(!window.confirm('Delete?'))return;await supabase.from('expenses').delete().eq('id',id);toast.success('Deleted');load();}

  const total=records.reduce((s,r)=>s+Number(r.amount),0);
  const opex=records.filter(r=>r.expense_categories?.type==='opex').reduce((s,r)=>s+Number(r.amount),0);
  const capex=records.filter(r=>r.expense_categories?.type==='capex').reduce((s,r)=>s+Number(r.amount),0);
  const MONTHS=['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1 style={{fontSize:20,fontWeight:800}}>📤 Expenses</h1><div style={{fontSize:13,color:'var(--text3)'}}>All church expenditure — OPEX and CAPEX</div></div>
        <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ Record Expense</button>
      </div>
      <div className="grid-3 mb-4">
        <StatCard icon="💸" label="Total Expenses" value={fmt(total)} color="red"/>
        <StatCard icon="⚙️" label="Operating (OPEX)" value={fmt(opex)} color="amber"/>
        <StatCard icon="🏗️" label="Capital (CAPEX)" value={fmt(capex)} color="blue"/>
      </div>
      <div className="card mb-4">
        <div className="card-pad" style={{paddingTop:10,paddingBottom:10}}>
          <div className="flex gap-2 flex-wrap">
            <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>{[2026,2025,2024,2023].map(y=><option key={y}>{y}</option>)}</select>
            <select className="form-select" style={{width:'auto'}} value={month} onChange={e=>setMonth(e.target.value)}>
              <option value="">All Months</option>{MONTHS.slice(1).map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select className="form-select" style={{width:'auto'}} value={journal} onChange={e=>setJournal(e.target.value)}>
              <option value="">All Journals</option>{JOURNALS.map(j=><option key={j} value={j} style={{textTransform:'capitalize'}}>{j.replace('_',' ')}</option>)}
            </select>
            <select className="form-select" style={{width:'auto'}} value={catF} onChange={e=>setCatF(e.target.value)}>
              <option value="">All Categories</option>{categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{fontSize:11,color:'var(--text3)',marginTop:6}}>{records.length} records · Total: <strong>{fmt(total)}</strong></div>
        </div>
      </div>
      {loading?<div style={{textAlign:'center',padding:28,color:'var(--text3)'}}>Loading…</div>:(
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Journal</th><th>Amount</th><th>Method</th><th>Ref/Receipt</th><th>Del</th></tr></thead>
            <tbody>
              {records.length===0&&<tr><td colSpan={8} style={{textAlign:'center',padding:28,color:'var(--text3)'}}>No expenses found.</td></tr>}
              {records.map(r=>(
                <tr key={r.id}>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(r.date)}</td>
                  <td style={{fontWeight:600,maxWidth:180}}>{r.description}</td>
                  <td style={{fontSize:12}}>{r.expense_categories?.name||'—'}<br/><span style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase'}}>{r.expense_categories?.type||''}</span></td>
                  <td style={{fontSize:12}}><span className="badge badge-gray" style={{textTransform:'capitalize'}}>{r.journal?.replace('_',' ')||'—'}</span></td>
                  <td style={{fontWeight:700,color:'var(--red-dark)'}}>{fmt(r.amount)}</td>
                  <td style={{fontSize:12}}>{PM_LABELS[r.payment_method]||r.payment_method}</td>
                  <td style={{fontSize:11,color:'var(--text3)'}}>{r.reference_no||r.receipt_no||'—'}</td>
                  <td><button className="btn btn-danger btn-sm" onClick={()=>deleteExp(r.id)}>×</button></td>
                </tr>
              ))}
              {records.length>0&&<tr style={{background:'#FFEBEE',fontWeight:700}}><td colSpan={4}>TOTAL</td><td style={{color:'var(--red-dark)'}}>{fmt(total)}</td><td colSpan={3}></td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {showAdd&&(
        <Modal title="Record Expense" onClose={()=>setShowAdd(false)} large
          footer={<><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="exp-form" type="submit" disabled={saving}>{saving?'Saving…':'Save Expense'}</button></>}>
          <form id="exp-form" onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input className="form-input" type="number" min="1" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required/></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Description *</label><input className="form-input" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Category</label><select className="form-select" value={form.category_id} onChange={e=>setForm(f=>({...f,category_id:e.target.value}))}><option value="">Select category…</option>{categories.map(c=><option key={c.id} value={c.id}>{c.name} ({c.type?.toUpperCase()})</option>)}</select></div>
              <div className="form-group"><label className="form-label">Journal / Department</label><select className="form-select" value={form.journal} onChange={e=>setForm(f=>({...f,journal:e.target.value}))}>{JOURNALS.map(j=><option key={j} value={j} style={{textTransform:'capitalize'}}>{j.replace('_',' ')}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Payment Method</label><select className="form-select" value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))}>{PAYMENT_METHODS.map(m=><option key={m} value={m}>{PM_LABELS[m]}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Reference No</label><input className="form-input" value={form.reference_no} onChange={e=>setForm(f=>({...f,reference_no:e.target.value}))} placeholder="Cheque / M-Pesa ref"/></div>
              <div className="form-group"><label className="form-label">Receipt No</label><input className="form-input" value={form.receipt_no} onChange={e=>setForm(f=>({...f,receipt_no:e.target.value}))}/></div>
              <div className="form-group"><label className="form-label">Approved By</label><input className="form-input" value={form.approved_by} onChange={e=>setForm(f=>({...f,approved_by:e.target.value}))} placeholder="Name of approver"/></div>
              <div className="form-group"><label className="form-label">Group (if group expense)</label><select className="form-select" value={form.group_id} onChange={e=>setForm(f=>({...f,group_id:e.target.value}))}><option value="">None</option>{groups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}</select></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PETTY CASH
// ════════════════════════════════════════════════════════════
function PettyCashPanel() {
  const {profile}=useAuth();
  const [records,setRecords]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showAdd,setShowAdd]=useState(false);
  const [saving,setSaving]=useState(false);
  const [form,setForm]=useState({date:new Date().toISOString().split('T')[0],type:'withdrawal',amount:'',description:'',reference:''});
  const balance=records.length>0?Number(records[0].balance):0;

  useEffect(()=>{load();},[]);
  async function load(){
    setLoading(true);
    const {data}=await supabase.from('petty_cash').select('*').order('created_at',{ascending:false});
    setRecords(data||[]); setLoading(false);
  }
  async function handleSave(e){
    e.preventDefault();setSaving(true);
    const prev=records[0]?.balance||0;
    const newBal=form.type==='replenishment'?Number(prev)+Number(form.amount):Number(prev)-Number(form.amount);
    if(newBal<0){toast.error('Insufficient petty cash balance');setSaving(false);return;}
    const {error}=await supabase.from('petty_cash').insert({...form,amount:Number(form.amount),balance:newBal,recorded_by:profile?.id});
    if(error)toast.error(error.message);
    else{toast.success('Petty cash updated ✅');setShowAdd(false);setForm({date:new Date().toISOString().split('T')[0],type:'withdrawal',amount:'',description:'',reference:''});load();}
    setSaving(false);
  }

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1 style={{fontSize:20,fontWeight:800}}>💵 Petty Cash</h1><div style={{fontSize:13,color:'var(--text3)'}}>Manage petty cash float and withdrawals</div></div>
        <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ Add Entry</button>
      </div>
      <div className="grid-3 mb-4">
        <StatCard icon="💵" label="Current Balance" value={fmt(balance)} color={balance>0?'green':'red'}/>
        <StatCard icon="📥" label="Total Replenishments" value={fmt(records.filter(r=>r.type==='replenishment').reduce((s,r)=>s+Number(r.amount),0))} color="blue"/>
        <StatCard icon="📤" label="Total Withdrawals" value={fmt(records.filter(r=>r.type==='withdrawal').reduce((s,r)=>s+Number(r.amount),0))} color="amber"/>
      </div>
      {loading?<div style={{textAlign:'center',padding:28,color:'var(--text3)'}}>Loading…</div>:(
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Amount</th><th>Balance After</th><th>Reference</th></tr></thead>
            <tbody>
              {records.length===0&&<tr><td colSpan={6} style={{textAlign:'center',padding:28,color:'var(--text3)'}}>No petty cash entries.</td></tr>}
              {records.map(r=>(
                <tr key={r.id}>
                  <td style={{whiteSpace:'nowrap'}}>{fmtDate(r.date)}</td>
                  <td><span className={`badge badge-${r.type==='replenishment'?'green':'red'}`} style={{textTransform:'capitalize'}}>{r.type}</span></td>
                  <td style={{fontWeight:600}}>{r.description}</td>
                  <td style={{fontWeight:700,color:r.type==='replenishment'?'var(--success)':'var(--red)'}}>{r.type==='withdrawal'?'-':'+' }{fmt(r.amount)}</td>
                  <td style={{fontWeight:700}}>{fmt(r.balance)}</td>
                  <td style={{fontSize:12,color:'var(--text3)'}}>{r.reference||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showAdd&&(
        <Modal title="Petty Cash Entry" onClose={()=>setShowAdd(false)}
          footer={<><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="pc-form" type="submit" disabled={saving}>{saving?'Saving…':'Save'}</button></>}>
          <form id="pc-form" onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Type *</label><select className="form-select" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}><option value="withdrawal">Withdrawal</option><option value="replenishment">Replenishment</option></select></div>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input className="form-input" type="number" min="1" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required/></div>
              <div className="form-group"><label className="form-label">Reference</label><input className="form-input" value={form.reference} onChange={e=>setForm(f=>({...f,reference:e.target.value}))}/></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Description *</label><input className="form-input" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} required/></div>
            </div>
            <div style={{padding:'10px 14px',background:balance>0?'var(--success-bg)':'#FFEBEE',borderRadius:8,fontSize:13}}>
              Current balance: <strong>{fmt(balance)}</strong>
              {form.amount&&<span style={{marginLeft:8}}>→ After: <strong>{fmt(form.type==='replenishment'?balance+Number(form.amount):balance-Number(form.amount))}</strong></span>}
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// BUDGETS
// ════════════════════════════════════════════════════════════
function BudgetsPanel() {
  const {profile}=useAuth();
  const [budgets,setBudgets]=useState([]);
  const [actuals,setActuals]=useState({});
  const [categories,setCategories]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showAdd,setShowAdd]=useState(false);
  const [saving,setSaving]=useState(false);
  const [year,setYear]=useState(new Date().getFullYear());
  const [form,setForm]=useState({year:new Date().getFullYear(),month:'',category_id:'',budgeted_amount:'',notes:''});

  useEffect(()=>{
    supabase.from('expense_categories').select('*').order('name').then(({data})=>setCategories(data||[]));
    load();
  },[year]);

  async function load(){
    setLoading(true);
    const [{data:budgData},{data:expData}]=await Promise.all([
      supabase.from('budgets').select('*,expense_categories(name,type)').eq('year',year).order('created_at'),
      supabase.from('expenses').select('amount,category_id').gte('date',`${year}-01-01`).lte('date',`${year}-12-31`),
    ]);
    setBudgets(budgData||[]);
    const act={};
    (expData||[]).forEach(e=>{if(e.category_id)act[e.category_id]=(act[e.category_id]||0)+Number(e.amount);});
    setActuals(act);
    setLoading(false);
  }

  async function handleSave(e){
    e.preventDefault();setSaving(true);
    const {error}=await supabase.from('budgets').insert({...form,month:form.month?Number(form.month):null,budgeted_amount:Number(form.budgeted_amount),created_by:profile?.id});
    if(error)toast.error(error.message);
    else{toast.success('Budget set ✅');setShowAdd(false);setForm({year:new Date().getFullYear(),month:'',category_id:'',budgeted_amount:'',notes:''});load();}
    setSaving(false);
  }
  async function deleteBudget(id){if(!window.confirm('Delete this budget?'))return;await supabase.from('budgets').delete().eq('id',id);toast.success('Deleted');load();}

  const totalBudget=budgets.reduce((s,b)=>s+Number(b.budgeted_amount),0);
  const totalActual=budgets.reduce((s,b)=>s+(actuals[b.category_id]||0),0);

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1 style={{fontSize:20,fontWeight:800}}>📋 Budgets</h1><div style={{fontSize:13,color:'var(--text3)'}}>Set and track annual and monthly expense budgets</div></div>
        <div className="flex gap-2">
          <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>{[2026,2025,2024,2023].map(y=><option key={y}>{y}</option>)}</select>
          <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ Set Budget</button>
        </div>
      </div>
      <div className="grid-3 mb-4">
        <StatCard icon="📋" label="Total Budget" value={fmt(totalBudget)} color="blue"/>
        <StatCard icon="📤" label="Actual Spent" value={fmt(totalActual)} color="red"/>
        <StatCard icon="💰" label="Remaining" value={fmt(totalBudget-totalActual)} color={totalBudget-totalActual>=0?'green':'red'}/>
      </div>
      {loading?<div style={{textAlign:'center',padding:28,color:'var(--text3)'}}>Loading…</div>:(
        <div className="table-wrap">
          <table>
            <thead><tr><th>Category</th><th>Type</th><th>Period</th><th>Budgeted</th><th>Actual</th><th>Variance</th><th>Usage</th><th>Del</th></tr></thead>
            <tbody>
              {budgets.length===0&&<tr><td colSpan={8} style={{textAlign:'center',padding:28,color:'var(--text3)'}}>No budgets set. Click + Set Budget to add one.</td></tr>}
              {budgets.map(b=>{
                const actual=actuals[b.category_id]||0;
                const variance=Number(b.budgeted_amount)-actual;
                const pct=b.budgeted_amount>0?Math.round((actual/Number(b.budgeted_amount))*100):0;
                const over=pct>100;
                return(
                  <tr key={b.id}>
                    <td style={{fontWeight:700}}>{b.expense_categories?.name||'—'}</td>
                    <td><span className={`badge badge-${b.expense_categories?.type==='capex'?'blue':'green'}`}>{b.expense_categories?.type?.toUpperCase()||'—'}</span></td>
                    <td style={{fontSize:12}}>{b.month?`Month ${b.month}`:'Annual'}</td>
                    <td style={{fontWeight:700}}>{fmt(b.budgeted_amount)}</td>
                    <td style={{fontWeight:700,color:over?'var(--red)':'var(--text)'}}>{fmt(actual)}</td>
                    <td style={{fontWeight:700,color:variance>=0?'var(--success)':'var(--red)'}}>{variance>=0?'+':''}{fmt(variance)}</td>
                    <td style={{minWidth:100}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <div style={{flex:1,height:8,background:'var(--bg)',borderRadius:4,overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${Math.min(pct,100)}%`,background:over?'var(--red)':'var(--blue)',borderRadius:4,transition:'width .3s'}}/>
                        </div>
                        <span style={{fontSize:11,fontWeight:700,color:over?'var(--red)':'var(--text3)',width:32}}>{pct}%</span>
                      </div>
                    </td>
                    <td><button className="btn btn-danger btn-sm" onClick={()=>deleteBudget(b.id)}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showAdd&&(
        <Modal title="Set Budget" onClose={()=>setShowAdd(false)}
          footer={<><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="budget-form" type="submit" disabled={saving}>{saving?'Saving…':'Set Budget'}</button></>}>
          <form id="budget-form" onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Year *</label><select className="form-select" value={form.year} onChange={e=>setForm(f=>({...f,year:Number(e.target.value)}))}>{[2026,2025,2024,2023].map(y=><option key={y}>{y}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Month (leave blank for annual)</label><select className="form-select" value={form.month} onChange={e=>setForm(f=>({...f,month:e.target.value}))}><option value="">Annual Budget</option>{[1,2,3,4,5,6,7,8,9,10,11,12].map(m=><option key={m} value={m}>{new Date(2024,m-1,1).toLocaleString('default',{month:'long'})}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Category *</label><select className="form-select" value={form.category_id} onChange={e=>setForm(f=>({...f,category_id:e.target.value}))} required><option value="">Select…</option>{categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Budgeted Amount (KES) *</label><input className="form-input" type="number" min="0" value={form.budgeted_amount} onChange={e=>setForm(f=>({...f,budgeted_amount:e.target.value}))} required/></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><input className="form-input" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CHART OF ACCOUNTS
// ════════════════════════════════════════════════════════════
function AccountsPanel() {
  const [accounts,setAccounts]=useState([]);
  const [loading,setLoading]=useState(true);
  const [typeF,setTypeF]=useState('');

  useEffect(()=>{
    supabase.from('accounts').select('*').order('code').then(({data})=>{setAccounts(data||[]);setLoading(false);});
  },[]);

  const filtered=accounts.filter(a=>!typeF||a.type===typeF);
  const groups=['asset','liability','equity','income','expense'];
  const TYPE_COLORS={asset:'blue',liability:'red',equity:'green',income:'amber',expense:'red'};

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1 style={{fontSize:20,fontWeight:800}}>📑 Chart of Accounts</h1><div style={{fontSize:13,color:'var(--text3)'}}>Standard double-entry bookkeeping account structure</div></div>
        <select className="form-select" style={{width:'auto'}} value={typeF} onChange={e=>setTypeF(e.target.value)}>
          <option value="">All Types</option>{groups.map(g=><option key={g} value={g} style={{textTransform:'capitalize'}}>{g}</option>)}
        </select>
      </div>
      {loading?<div style={{textAlign:'center',padding:28,color:'var(--text3)'}}>Loading…</div>:(
        groups.filter(g=>!typeF||g===typeF).map(grp=>{
          const grpAccounts=filtered.filter(a=>a.type===grp);
          if(!grpAccounts.length) return null;
          return(
            <div key={grp} className="card mb-4" style={{overflow:'hidden'}}>
              <div style={{padding:'11px 16px',background:'var(--blue-dark)',color:'white',fontWeight:700,fontSize:13,textTransform:'capitalize'}}>
                {grp} Accounts ({grpAccounts.length})
              </div>
              <table>
                <thead><tr><th>Code</th><th>Account Name</th><th>Category</th><th>Sub-Category</th><th>Description</th></tr></thead>
                <tbody>
                  {grpAccounts.map(a=>(
                    <tr key={a.id}>
                      <td style={{fontFamily:'monospace',fontWeight:700,color:'var(--blue-dark)'}}>{a.code}</td>
                      <td style={{fontWeight:600}}>{a.name}</td>
                      <td style={{fontSize:12}}>{a.category}</td>
                      <td style={{fontSize:12,color:'var(--text3)'}}>{a.sub_category||'—'}</td>
                      <td style={{fontSize:12,color:'var(--text3)'}}>{a.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// FINANCIAL REPORTS
// ════════════════════════════════════════════════════════════
function FinancialReportsPanel() {
  const [year,setYear]=useState(new Date().getFullYear());
  const [month,setMonth]=useState('');
  const [report,setReport]=useState(null);
  const [loading,setLoading]=useState(false);

  const MONTHS=['','January','February','March','April','May','June','July','August','September','October','November','December'];

  async function generateReport() {
    setLoading(true);
    const yStart=`${year}-01-01`,yEnd=`${year}-12-31`;
    const mStr=month?`${year}-${String(month).padStart(2,'0')}`:'';
    const dStart=month?`${mStr}-01`:yStart;
    const dEnd=month?`${mStr}-31`:yEnd;

    const [
      {data:contribs},{data:expenses},{data:income},{data:distContribs},{data:monthlyC},{data:budgets}
    ]=await Promise.all([
      supabase.from('contributions').select('type,amount,members(districts(name))').gte('date',dStart).lte('date',dEnd),
      supabase.from('expenses').select('amount,journal,expense_categories(name,type)').gte('date',dStart).lte('date',dEnd),
      supabase.from('income_entries').select('amount,category').gte('date',dStart).lte('date',dEnd),
      supabase.from('contributions').select('amount,type,members(districts(name))').gte('date',yStart).lte('date',yEnd),
      supabase.from('contributions').select('date,amount,type').gte('date',yStart).lte('date',yEnd),
      supabase.from('budgets').select('budgeted_amount,expense_categories(name)').eq('year',year),
    ]);

    const sumAmt=(arr)=>(arr||[]).reduce((s,r)=>s+Number(r.amount),0);
    const sumType=(arr,t)=>(arr||[]).filter(r=>r.type===t).reduce((s,r)=>s+Number(r.amount),0);

    // District breakdown
    const byDist={};
    (distContribs||[]).forEach(c=>{
      const d=c.members?.districts?.name||'Unknown';
      if(!byDist[d])byDist[d]={tithe:0,offering:0,total:0};
      if(c.type==='Tithe')byDist[d].tithe+=Number(c.amount);
      else byDist[d].offering+=Number(c.amount);
      byDist[d].total+=Number(c.amount);
    });

    // By journal
    const byJournal={};
    (expenses||[]).forEach(e=>{byJournal[e.journal]=(byJournal[e.journal]||0)+Number(e.amount);});

    // Monthly trend
    const monthly=Array.from({length:12},(_,i)=>{
      const m=String(i+1).padStart(2,'0');
      const mc=(monthlyC||[]).filter(c=>c.date?.startsWith(`${year}-${m}`));
      return{month:new Date(year,i,1).toLocaleString('default',{month:'short'}),tithe:mc.filter(c=>c.type==='Tithe').reduce((s,c)=>s+Number(c.amount),0),offering:mc.filter(c=>c.type!=='Tithe').reduce((s,c)=>s+Number(c.amount),0)};
    });

    const totalIncome=sumAmt(contribs)+sumAmt(income);
    const totalExpense=sumAmt(expenses);
    const surplus=totalIncome-totalExpense;

    setReport({
      period:month?`${MONTHS[month]} ${year}`:`Full Year ${year}`,
      totalIncome,totalExpense,surplus,
      tithes:sumType(contribs,'Tithe'),offerings:sumType(contribs,'Offering'),
      thanksgiving:sumType(contribs,'Thanksgiving'),harvest:sumType(contribs,'Harvest'),
      building:sumType(contribs,'Building Fund'),special:sumType(contribs,'Special'),
      otherIncome:sumAmt(income),
      opex:(expenses||[]).filter(e=>e.expense_categories?.type==='opex').reduce((s,e)=>s+Number(e.amount),0),
      capex:(expenses||[]).filter(e=>e.expense_categories?.type==='capex').reduce((s,e)=>s+Number(e.amount),0),
      byDist,byJournal,monthly,
      totalBudget:(budgets||[]).reduce((s,b)=>s+Number(b.budgeted_amount),0),
    });
    setLoading(false);
  }

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1 style={{fontSize:20,fontWeight:800}}>📄 Financial Reports</h1><div style={{fontSize:13,color:'var(--text3)'}}>Income statements, balance summaries, district reports</div></div>
        <div className="flex gap-2">
          <button className="btn btn-outline btn-sm no-print" onClick={()=>window.print()}>🖨️ Print</button>
        </div>
      </div>

      {/* Period selector */}
      <div className="card mb-4">
        <div className="card-pad">
          <div className="flex gap-2 flex-wrap align-center">
            <div>
              <div className="form-label">Year</div>
              <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>
                {[2026,2025,2024,2023].map(y=><option key={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <div className="form-label">Month (optional)</div>
              <select className="form-select" style={{width:'auto'}} value={month} onChange={e=>setMonth(e.target.value)}>
                <option value="">Full Year</option>
                {MONTHS.slice(1).map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div style={{alignSelf:'flex-end'}}>
              <button className="btn btn-primary" onClick={generateReport} disabled={loading}>{loading?'Generating…':'Generate Report'}</button>
            </div>
          </div>
        </div>
      </div>

      {report&&(
        <div>
          <div style={{textAlign:'center',marginBottom:24}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5}}>PCEA Thingati Church</div>
            <div style={{fontSize:20,fontWeight:800,marginTop:4}}>Financial Report — {report.period}</div>
          </div>

          {/* Income Statement */}
          <div className="report-section mb-4">
            <div className="report-section-hdr">📥 INCOME STATEMENT</div>
            <div className="report-body">
              <div className="grid-2">
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',marginBottom:10}}>Revenue — Contributions</div>
                  {[['Tithes',report.tithes],['Sunday Offerings',report.offerings],['Thanksgiving Offerings',report.thanksgiving],['Harvest Offerings',report.harvest],['Building Fund',report.building],['Special Contributions',report.special],['Other Income',report.otherIncome]].map(([l,v])=>(
                    <div key={l} className="kv-row"><span>{l}</span><span>{fmt(v)}</span></div>
                  ))}
                  <div className="kv-row" style={{fontWeight:800,borderTop:'2px solid var(--border)',marginTop:4,paddingTop:8}}>
                    <span>TOTAL INCOME</span><span style={{color:'var(--blue-dark)'}}>{fmt(report.totalIncome)}</span>
                  </div>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',marginBottom:10}}>Expenditure by Journal</div>
                  {Object.entries(report.byJournal).sort((a,b)=>b[1]-a[1]).map(([j,amt])=>(
                    <div key={j} className="kv-row"><span style={{textTransform:'capitalize'}}>{j.replace('_',' ')}</span><span>{fmt(amt)}</span></div>
                  ))}
                  <div className="kv-row" style={{fontWeight:800,borderTop:'2px solid var(--border)',marginTop:4,paddingTop:8}}>
                    <span>TOTAL EXPENDITURE</span><span style={{color:'var(--red-dark)'}}>{fmt(report.totalExpense)}</span>
                  </div>
                </div>
              </div>
              <div style={{marginTop:16,padding:'14px 18px',background:report.surplus>=0?'var(--success-bg)':'#FFEBEE',borderRadius:10,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontWeight:700,fontSize:15}}>NET SURPLUS / (DEFICIT)</span>
                <span style={{fontWeight:800,fontSize:20,color:report.surplus>=0?'var(--success)':'var(--red)'}}>{fmt(report.surplus)}</span>
              </div>
            </div>
          </div>

          {/* OPEX vs CAPEX */}
          <div className="report-section mb-4">
            <div className="report-section-hdr">⚙️ OPEX vs CAPEX</div>
            <div className="report-body">
              {[['Operating Expenses (OPEX)',report.opex,true],['Capital Expenditure (CAPEX)',report.capex,false]].map(([l,v,isOpex])=>(
                <div key={l} className="kv-row"><span>{l}</span><span style={{fontWeight:700,color:isOpex?'var(--text)':'var(--blue-dark)'}}>{fmt(v)}</span></div>
              ))}
              <div className="kv-row" style={{fontWeight:800}}><span>Total Expenditure</span><span style={{color:'var(--red-dark)'}}>{fmt(report.totalExpense)}</span></div>
            </div>
          </div>

          {/* District breakdown */}
          <div className="report-section mb-4">
            <div className="report-section-hdr">📍 CONTRIBUTIONS BY DISTRICT</div>
            <div className="report-body">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead><tr style={{background:'var(--bg)'}}><th style={{padding:'8px',textAlign:'left'}}>District</th><th style={{padding:'8px',textAlign:'right'}}>Tithes</th><th style={{padding:'8px',textAlign:'right'}}>Offerings</th><th style={{padding:'8px',textAlign:'right'}}>Total</th></tr></thead>
                <tbody>
                  {Object.entries(report.byDist).sort((a,b)=>b[1].total-a[1].total).map(([d,v])=>(
                    <tr key={d} style={{borderBottom:'1px solid var(--border)'}}>
                      <td style={{padding:'8px',fontWeight:600}}>{d}</td>
                      <td style={{padding:'8px',textAlign:'right'}}>{fmt(v.tithe)}</td>
                      <td style={{padding:'8px',textAlign:'right'}}>{fmt(v.offering)}</td>
                      <td style={{padding:'8px',textAlign:'right',fontWeight:700}}>{fmt(v.total)}</td>
                    </tr>
                  ))}
                  <tr style={{background:'var(--blue-light)',fontWeight:800}}>
                    <td style={{padding:'8px'}}>TOTAL</td>
                    <td style={{padding:'8px',textAlign:'right'}}>{fmt(Object.values(report.byDist).reduce((s,v)=>s+v.tithe,0))}</td>
                    <td style={{padding:'8px',textAlign:'right'}}>{fmt(Object.values(report.byDist).reduce((s,v)=>s+v.offering,0))}</td>
                    <td style={{padding:'8px',textAlign:'right'}}>{fmt(report.totalIncome)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly trend */}
          <div className="report-section mb-4">
            <div className="report-section-hdr">📈 MONTHLY TITHE & OFFERING TREND — {year}</div>
            <div className="report-body">
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'var(--bg)'}}><th style={{padding:'7px 10px',textAlign:'left'}}>Month</th><th style={{padding:'7px 10px',textAlign:'right'}}>Tithes</th><th style={{padding:'7px 10px',textAlign:'right'}}>Offerings</th><th style={{padding:'7px 10px',textAlign:'right'}}>Total</th></tr></thead>
                  <tbody>
                    {report.monthly.map(m=>(
                      <tr key={m.month} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'7px 10px',fontWeight:600}}>{m.month}</td>
                        <td style={{padding:'7px 10px',textAlign:'right'}}>{fmt(m.tithe)}</td>
                        <td style={{padding:'7px 10px',textAlign:'right'}}>{fmt(m.offering)}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',fontWeight:700}}>{fmt(m.tithe+m.offering)}</td>
                      </tr>
                    ))}
                    <tr style={{background:'var(--blue-light)',fontWeight:800}}>
                      <td style={{padding:'7px 10px'}}>TOTAL</td>
                      <td style={{padding:'7px 10px',textAlign:'right'}}>{fmt(report.monthly.reduce((s,m)=>s+m.tithe,0))}</td>
                      <td style={{padding:'7px 10px',textAlign:'right'}}>{fmt(report.monthly.reduce((s,m)=>s+m.offering,0))}</td>
                      <td style={{padding:'7px 10px',textAlign:'right'}}>{fmt(report.monthly.reduce((s,m)=>s+m.tithe+m.offering,0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Budget vs Actual */}
          {report.totalBudget>0&&(
            <div className="report-section mb-4">
              <div className="report-section-hdr">📋 BUDGET vs ACTUAL — {year}</div>
              <div className="report-body">
                {[['Annual Budget',report.totalBudget],['Actual Expenditure',report.totalExpense],['Variance',report.totalBudget-report.totalExpense]].map(([l,v])=>(
                  <div key={l} className="kv-row"><span>{l}</span><span style={{fontWeight:700,color:l==='Variance'?(v>=0?'var(--success)':'var(--red)'):'var(--text)'}}>{fmt(v)}</span></div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// AUDIT LOG
// ════════════════════════════════════════════════════════════
function AuditPanel() {
  const [logs,setLogs]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    supabase.from('audit_log').select('*').order('created_at',{ascending:false}).limit(100)
      .then(({data})=>{setLogs(data||[]);setLoading(false);});
  },[]);

  const ACTION_COLOR={insert:'badge-green',update:'badge-blue',delete:'badge-red'};

  return (
    <div>
      <div className="mb-4"><h1 style={{fontSize:20,fontWeight:800}}>🔍 Audit Log</h1><div style={{fontSize:13,color:'var(--text3)'}}>Track all financial record changes — last 100 entries</div></div>
      {loading?<div style={{textAlign:'center',padding:28,color:'var(--text3)'}}>Loading…</div>:(
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date/Time</th><th>Action</th><th>Table</th><th>User</th><th>Details</th></tr></thead>
            <tbody>
              {logs.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:28,color:'var(--text3)'}}>No audit entries yet. Changes to financial records will appear here.</td></tr>}
              {logs.map(l=>(
                <tr key={l.id}>
                  <td style={{whiteSpace:'nowrap',fontSize:12}}>{new Date(l.created_at).toLocaleString('en-KE')}</td>
                  <td><span className={`badge ${ACTION_COLOR[l.action]||'badge-gray'}`} style={{textTransform:'capitalize'}}>{l.action}</span></td>
                  <td style={{fontSize:12,fontFamily:'monospace'}}>{l.table_name}</td>
                  <td style={{fontSize:12}}>{l.user_email||'—'}</td>
                  <td style={{fontSize:11,color:'var(--text3)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{JSON.stringify(l.new_data||l.old_data||{})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
